import { useState } from 'react'
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import {
  REFERENCE_CHARSETS,
  TRANSFER_REFERENCE_MAX,
  TRANSFER_TYPE_CODE,
  TRANSFER_TYPE_NAME_MAX,
  TRANSFER_TYPE_SORT_MAX,
  createTransferType,
  referenceLabel,
  transferTypeName,
  updateTransferType,
  useTransferTypes,
  type CashTransferType,
  type ReferenceCharset,
  type TransferTypeDraft,
} from '@/features/cashdesk/transferTypeApi'

interface TypeForm {
  code: string
  name: string
  referenceRequired: boolean
  referenceLabel: string
  referenceCharset: ReferenceCharset
  referenceMinLength: number | null
  referenceMaxLength: number | null
  sortOrder: number | null
  active: boolean
}

export function TransferTypesPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<TypeForm>()
  const [editing, setEditing] = useState<CashTransferType | 'new' | null>(null)
  const current = editing !== null && editing !== 'new' ? editing : null
  const referenceRequired = Form.useWatch('referenceRequired', form)
  const types = useTransferTypes()

  const save = useMutation({
    mutationFn: (values: TypeForm) => {
      const required = current?.referenceRequired ?? values.referenceRequired
      const draft: TransferTypeDraft = {
        code: current?.code ?? values.code.trim(),
        name: values.name.trim(),
        referenceRequired: required,
        referenceLabel: required ? values.referenceLabel.trim() || null : null,
        referenceCharset: required ? values.referenceCharset : 'ANY',
        referenceMinLength: required ? values.referenceMinLength : null,
        referenceMaxLength: required ? values.referenceMaxLength : null,
        sortOrder: values.sortOrder ?? 1,
        active: values.active,
      }
      return current ? updateTransferType(current.id, draft) : createTransferType(draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cashdesk.transferTypes })
      setEditing(null)
    },
  })

  const open = (target: CashTransferType | 'new') => {
    save.reset()
    setEditing(target)
    const nextSort = Math.min(TRANSFER_TYPE_SORT_MAX, Math.max(0, ...(types.data ?? []).map((type) => type.sortOrder)) + 1)
    form.setFieldsValue(
      target === 'new'
        ? {
            code: '',
            name: '',
            referenceRequired: true,
            referenceLabel: '',
            referenceCharset: 'ANY',
            referenceMinLength: null,
            referenceMaxLength: null,
            sortOrder: nextSort,
            active: true,
          }
        : {
            code: target.code,
            name: target.name,
            referenceRequired: target.referenceRequired,
            referenceLabel: target.referenceLabel ?? '',
            referenceCharset: target.referenceCharset,
            referenceMinLength: target.referenceMinLength ?? null,
            referenceMaxLength: target.referenceMaxLength ?? null,
            sortOrder: target.sortOrder,
            active: target.active,
          },
    )
  }

  const columns: ColumnsType<CashTransferType> = [
    { key: 'sortOrder', title: t('transferTypes.sortOrder'), width: 90, align: 'right', render: (_, row) => row.sortOrder },
    {
      key: 'name',
      title: t('transferTypes.name'),
      render: (_, row) => (
        <Space size={6} wrap>
          <span>{transferTypeName(row.code, types.data, t)}</span>
          {row.system ? <Tag>{t('transferTypes.system')}</Tag> : null}
        </Space>
      ),
    },
    {
      key: 'code',
      title: t('transferTypes.code'),
      width: 150,
      render: (_, row) => <Typography.Text code>{row.code}</Typography.Text>,
    },
    {
      key: 'reference',
      title: t('transferTypes.reference'),
      width: 300,
      render: (_, row) =>
        row.referenceRequired ? (
          <Space direction="vertical" size={0}>
            <span>{referenceLabel(row, t)}</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {[
                t(`transferTypes.charset.${row.referenceCharset}`),
                t('transferTypes.lengthRange', {
                  min: row.referenceMinLength ?? 1,
                  max: row.referenceMaxLength ?? TRANSFER_REFERENCE_MAX,
                }),
              ].join(' · ')}
            </Typography.Text>
          </Space>
        ) : (
          <Typography.Text type="secondary">{t('transferTypes.noReference')}</Typography.Text>
        ),
    },
    {
      key: 'state',
      title: t('transferTypes.state'),
      width: 120,
      render: (_, row) =>
        row.active ? <Tag color="green">{t('transferTypes.active')}</Tag> : <Tag>{t('transferTypes.inactive')}</Tag>,
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions items={[{ key: 'edit', label: t('common.edit'), icon: <EditOutlined />, onClick: () => open(row) }]} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.transferTypes')}
        description={t('pageIntro.transferTypes')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open('new')}>
            {t('transferTypes.add')}
          </Button>
        }
      />

      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('transferTypes.rule')} />
        {types.isError ? <Alert type="error" showIcon message={translateError(types.error, t)} /> : null}
        <Table<CashTransferType>
          rowKey={(row) => row.id}
          loading={types.isPending}
          dataSource={types.data ?? []}
          columns={columns}
          pagination={false}
          scroll={{ x: 860 }}
          locale={{ emptyText: t('transferTypes.empty') }}
        />
      </Space>

      <Modal
        open={editing !== null}
        title={editing === 'new' ? t('transferTypes.addTitle') : t('transferTypes.editTitle')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        confirmLoading={save.isPending}
        onCancel={() => setEditing(null)}
        destroyOnHidden
        onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {save.isError ? <Alert type="error" showIcon message={translateError(save.error, t)} /> : null}
          {current?.system ? <Alert type="info" showIcon message={t('transferTypes.systemLocked')} /> : null}
          <Form<TypeForm> form={form} layout="vertical" requiredMark preserve={false}>
            <Form.Item
              name="code"
              label={t('transferTypes.code')}
              extra={current ? undefined : t('transferTypes.codeHint')}
              normalize={(value: string) => value.toUpperCase()}
              rules={[
                { required: true, whitespace: true, message: t('transferTypes.codeRequired') },
                { pattern: TRANSFER_TYPE_CODE, message: t('transferTypes.codeInvalid') },
              ]}
            >
              <Input maxLength={32} disabled={current !== null} />
            </Form.Item>
            <Form.Item
              name="name"
              label={t('transferTypes.name')}
              rules={[{ required: true, whitespace: true, message: t('transferTypes.nameRequired') }]}
            >
              <Input maxLength={TRANSFER_TYPE_NAME_MAX} />
            </Form.Item>
            <Form.Item
              name="referenceRequired"
              label={t('transferTypes.referenceRequired')}
              extra={t('transferTypes.referenceRequiredHint')}
              valuePropName="checked"
            >
              <Switch disabled={current !== null} />
            </Form.Item>
            {referenceRequired ? (
              <>
                <Form.Item name="referenceLabel" label={t('transferTypes.referenceLabel')}>
                  <Input maxLength={TRANSFER_TYPE_NAME_MAX} placeholder={t('transferTypes.referenceLabelPlaceholder')} />
                </Form.Item>
                <Form.Item name="referenceCharset" label={t('transferTypes.charsetLabel')}>
                  <Select<ReferenceCharset>
                    options={REFERENCE_CHARSETS.map((value) => ({ value, label: t(`transferTypes.charset.${value}`) }))}
                  />
                </Form.Item>
                <Space.Compact block>
                  <Form.Item name="referenceMinLength" label={t('transferTypes.minLength')} style={{ flex: 1 }}>
                    <InputNumber min={1} max={TRANSFER_REFERENCE_MAX} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="referenceMaxLength"
                    label={t('transferTypes.maxLength')}
                    style={{ flex: 1 }}
                    dependencies={['referenceMinLength']}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator: (_, value: number | null) => {
                          const min = getFieldValue('referenceMinLength') as number | null
                          return value != null && min != null && min > value
                            ? Promise.reject(new Error(t('transferTypes.lengthInvalid')))
                            : Promise.resolve()
                        },
                      }),
                    ]}
                  >
                    <InputNumber min={1} max={TRANSFER_REFERENCE_MAX} precision={0} style={{ width: '100%' }} />
                  </Form.Item>
                </Space.Compact>
              </>
            ) : null}
            <Form.Item
              name="sortOrder"
              label={t('transferTypes.sortOrder')}
              rules={[{ required: true, message: t('transferTypes.sortOrderRequired') }]}
            >
              <InputNumber min={1} max={TRANSFER_TYPE_SORT_MAX} precision={0} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="active" label={t('transferTypes.active')} valuePropName="checked">
              <Switch disabled={current?.system ?? false} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </>
  )
}
